import {
  Component,
  inject,
  signal,
  computed,
  HostListener,
  ChangeDetectionStrategy,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import {
  ActivatedRoute,
  NavigationEnd,
  Router,
  RouterLink,
  RouterLinkActive,
} from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { getDeepestHelpTopic } from '../../../core/help/help-route-data';
import { isAssessmentHelpRole } from '../../../core/help/help-topic.model';
import { AuthStore } from '../../../core/store/auth.store';
import { APP_BRAND } from '../../../core/branding/brand.config';
import { resetDemoData } from '../../../core/demo/demo-control';

interface NavItem {
  path: string;
  icon: string;
  label: string;
}

@Component({
  selector: 'app-header',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [RouterLink, RouterLinkActive, MatIconModule],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
})
export class HeaderComponent {
  // Keep field name as 'auth' so the template binding (auth.logout(), auth.currentUser(), etc.) needs no change
  protected auth = inject(AuthStore);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  protected readonly brand = APP_BRAND;

  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map((event) => (event as NavigationEnd).urlAfterRedirects)
    ),
    { initialValue: this.router.url }
  );

  mobileMenuOpen = signal(false);
  userMenuOpen = signal(false);

  navItems = computed(() => {
    const items: NavItem[] = [];
    if (this.auth.hasRole(['EDUCATOR', 'SITEMANAGER'])) {
      items.push(
        { path: '/dashboard', icon: 'dashboard', label: 'Dashboard' },
        { path: '/children', icon: 'group', label: 'Children' },
        { path: '/assessments', icon: 'assignment', label: 'Assessments' }
      );
    }
    if (this.auth.hasRole(['SITEMANAGER'])) {
      items.push({ path: '/educators', icon: 'manage_accounts', label: 'Educators' });
    }
    if (this.auth.hasRole(['SUPERUSER'])) {
      items.push({ path: '/admin', icon: 'admin_panel_settings', label: 'Admin' });
    }
    return items;
  });

  homeRoute = computed(() => (this.auth.hasRole(['SUPERUSER']) ? '/admin' : '/dashboard'));

  hasAssignedDocsAccess = computed(() =>
    this.auth.hasRole(['EDUCATOR', 'SITEMANAGER', 'SUPERUSER', 'ADMIN', 'ORGMANAGER'])
  );

  roleLabel = computed(() => {
    switch (this.auth.currentUserRole()) {
      case 'SITEMANAGER':
        return 'Site Manager';
      case 'SUPERUSER':
        return 'Super User';
      case 'ADMIN':
        return 'Admin';
      case 'ORGMANAGER':
        return 'Organisation manager';
      case 'NOTASSIGNED':
        return 'Not Assigned';
      default:
        return 'Educator';
    }
  });

  userInitials = computed(() => {
    const user = this.auth.currentUser();
    if (user?.FirstName && user?.LastName) {
      return (user.FirstName[0] + user.LastName[0]).toUpperCase();
    }
    return (user?.Email ?? '').substring(0, 2).toUpperCase();
  });

  userName = computed(() => {
    const user = this.auth.currentUser();
    if (user?.FirstName && user?.LastName) {
      return `${user.FirstName} ${user.LastName}`;
    }
    return user?.Email ?? '';
  });

  helpQueryParams = computed(() => {
    const currentUrl = this.currentUrl();
    if (currentUrl.startsWith('/help')) {
      return null;
    }

    const role = this.auth.currentUserRole();
    if (!isAssessmentHelpRole(role)) {
      return { topic: 'account-access' };
    }

    const topic = getDeepestHelpTopic(this.activatedRoute.snapshot);
    return topic ? { topic } : null;
  });

  protected onSiteChange(event: Event): void {
    const value = Number((event.target as HTMLSelectElement).value);
    if (Number.isInteger(value)) {
      this.auth.setSelectedOrganization(value);
    }
  }

  public toggleMobileMenu(): void {
    this.mobileMenuOpen.update((v) => !v);
  }

  public toggleUserMenu(): void {
    this.userMenuOpen.update((v) => !v);
  }

  public closeUserMenu(): void {
    this.userMenuOpen.set(false);
  }

  public closeMobileMenu(): void {
    this.mobileMenuOpen.set(false);
  }

  protected resetDemo(): void {
    if (!resetDemoData()) return;
    this.closeUserMenu();
    this.closeMobileMenu();
    this.auth.logout();
  }

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.userMenuOpen.set(false);
    this.mobileMenuOpen.set(false);
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.user-menu-wrapper')) {
      this.userMenuOpen.set(false);
    }
  }
}
